import { BaseApiService, ApiResponse } from './base-api';
import { API_CONFIG } from '../config/api';
import {
  PasskeyCredential,
  PasskeyRegistrationStart,
  PasskeyRegistrationStartResponse,
  PasskeyRegistrationComplete,
  PasskeyRegistrationCompleteResponse
} from '../types/auth';
import apiClient from './api';

export class Auth0PasskeyService extends BaseApiService {
  constructor() {
    super(API_CONFIG.baseURL);
  }

  /**
   * Start passkey registration process
   * @param request Registration start request
   * @returns Registration challenge and options
   */
  async startPasskeyRegistration(
    request: PasskeyRegistrationStart & { hotel_id?: string }
  ): Promise<ApiResponse<PasskeyRegistrationStartResponse & { enrollment_url?: string; user_id?: string }>> {
    console.log('🔐 Starting passkey registration for:', request.email);
    
    try {
      const response = await apiClient.post<PasskeyRegistrationStartResponse & { enrollment_url?: string; user_id?: string }>(
        '/auth0/passkey/register/start',
        request
      ) as any;
      
      if (response.success) {
        console.log('✅ Passkey registration started successfully');
        // Store session ID temporarily for completion (only if using WebAuthn flow)
        if (response.sessionId) {
          sessionStorage.setItem('passkey_session_id', response.sessionId);
        }
        // For Auth0 hosted flow, log the enrollment URL
        if (response.enrollment_url) {
          console.log('🔗 Auth0 enrollment URL received:', response.enrollment_url);
        }
      }
      
      return response as any; // Type assertion to match expected return type
    } catch (error) {
      console.error('❌ Failed to start passkey registration:', error);
      return {
        success: false,
        error: 'PASSKEY_REGISTRATION_START_FAILED',
        message: 'Failed to start passkey registration. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Complete passkey registration process
   * @param request Registration completion request
   * @returns Registration result
   */
  async completePasskeyRegistration(
    request: PasskeyRegistrationComplete
  ): Promise<ApiResponse<PasskeyRegistrationCompleteResponse>> {
    console.log('🔐 Completing passkey registration...');
    
    try {
      // Convert PublicKeyCredential to a serializable format
      const credentialData = {
        id: request.credential.id,
        rawId: this.arrayBufferToBase64(request.credential.rawId),
        type: request.credential.type,
        response: {
          clientDataJSON: this.arrayBufferToBase64(request.credential.response.clientDataJSON),
          attestationObject: this.arrayBufferToBase64(
            (request.credential.response as AuthenticatorAttestationResponse).attestationObject
          )
        }
      };

      const requestData = {
        session_id: request.sessionId,
        credential_response: credentialData,
        name: request.name
      };

      console.log('📤 Sending completion request:', {
        session_id: requestData.session_id,
        credential_response: '(credential data)',
        name: requestData.name
      });

      const response = await apiClient.post<PasskeyRegistrationCompleteResponse>(
        '/auth0/passkey/register/complete',
        requestData
      );
      
      if (response.success) {
        console.log('✅ Passkey registration completed successfully');
        // Clean up temporary session storage
        sessionStorage.removeItem('passkey_session_id');
      }
      
      return response as any; // Type assertion to match expected return type
    } catch (error) {
      console.error('❌ Failed to complete passkey registration:', error);
      return {
        success: false,
        error: 'PASSKEY_REGISTRATION_COMPLETE_FAILED',
        message: 'Failed to complete passkey registration. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * List user's registered passkeys
   * @returns List of user's passkeys
   */
  async listPasskeys(): Promise<ApiResponse<PasskeyCredential[]>> {
    console.log('📋 Fetching user passkeys...');
    
    try {
      const response = await this.get<PasskeyCredential[]>(
        '/auth0/passkey/list',
        { requireAuth: true }
      );
      
      if (response.success) {
        console.log(`✅ Retrieved ${response.data?.length || 0} passkeys`);
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to fetch passkeys:', error);
      return {
        success: false,
        error: 'PASSKEY_LIST_FAILED',
        message: 'Failed to retrieve passkeys. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Delete a specific passkey
   * @param credentialId ID of the credential to delete
   * @returns Deletion result
   */
  async deletePasskey(credentialId: string): Promise<ApiResponse<{ success: boolean }>> {
    console.log('🗑️ Deleting passkey:', credentialId);
    
    try {
      const response = await this.delete<{ success: boolean }>(
        `/auth0/passkey/${credentialId}`,
        { requireAuth: true }
      );
      
      if (response.success) {
        console.log('✅ Passkey deleted successfully');
      }
      
      return response;
    } catch (error) {
      console.error('❌ Failed to delete passkey:', error);
      return {
        success: false,
        error: 'PASSKEY_DELETE_FAILED',
        message: 'Failed to delete passkey. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Check if WebAuthn is supported by the browser
   * @returns Whether WebAuthn is supported
   */
  isWebAuthnSupported(): boolean {
    return !!(
      window.PublicKeyCredential &&
      window.navigator.credentials &&
      window.navigator.credentials.create &&
      window.navigator.credentials.get
    );
  }

  /**
   * Check if the platform supports passkeys
   * @returns Promise resolving to whether platform supports passkeys
   */
  async isPlatformAuthenticatorSupported(): Promise<boolean> {
    try {
      if (!this.isWebAuthnSupported()) {
        return false;
      }
      
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
      console.error('Failed to check platform authenticator support:', error);
      return false;
    }
  }

  /**
   * Create a new passkey credential using WebAuthn API
   * @param options PublicKeyCredentialCreationOptions from the server
   * @returns Promise resolving to the created credential
   */
  async createPasskeyCredential(
    options: PublicKeyCredentialCreationOptions
  ): Promise<PublicKeyCredential> {
    console.log('🔐 Creating passkey credential...');
    
    if (!this.isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported by this browser');
    }

    try {
      // Convert base64 strings to ArrayBuffers
      const createOptions: CredentialCreationOptions = {
        publicKey: {
          ...options,
          challenge: this.base64ToArrayBuffer(options.challenge as any),
          user: {
            ...options.user,
            id: this.base64ToArrayBuffer(options.user.id as any)
          }
        }
      };

      const credential = await navigator.credentials.create(createOptions) as PublicKeyCredential;
      
      if (!credential) {
        throw new Error('Failed to create passkey credential');
      }

      console.log('✅ Passkey credential created successfully');
      return credential;
    } catch (error) {
      console.error('❌ Failed to create passkey credential:', error);
      throw error;
    }
  }

  /**
   * Authenticate using an existing passkey
   * @param options PublicKeyCredentialRequestOptions from the server
   * @returns Promise resolving to the authentication credential
   */
  async authenticateWithPasskey(
    options: PublicKeyCredentialRequestOptions
  ): Promise<PublicKeyCredential> {
    console.log('🔐 Authenticating with passkey...');
    
    if (!this.isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported by this browser');
    }

    try {
      // Convert base64 strings to ArrayBuffers
      const requestOptions: CredentialRequestOptions = {
        publicKey: {
          ...options,
          challenge: this.base64ToArrayBuffer(options.challenge as any),
          allowCredentials: options.allowCredentials?.map(cred => ({
            ...cred,
            id: this.base64ToArrayBuffer(cred.id as any)
          }))
        }
      };

      const credential = await navigator.credentials.get(requestOptions) as PublicKeyCredential;
      
      if (!credential) {
        throw new Error('Failed to authenticate with passkey');
      }

      console.log('✅ Passkey authentication successful');
      return credential;
    } catch (error) {
      console.error('❌ Failed to authenticate with passkey:', error);
      throw error;
    }
  }

  // Utility methods for converting between ArrayBuffer and base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

// Export singleton instance
export const auth0PasskeyService = new Auth0PasskeyService();